import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';

export const FeatureDailyReport = () => {
  const { t } = useTranslation('landing');
  const m = 'featureReport.mockup';

  return (
    <Box component="section" sx={{ py: { xs: '60px', md: '100px' }, background: '#f8fafc', overflow: 'hidden' }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: '20px', md: '40px' } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '62% 35%' }, gap: '3%', alignItems: 'center' }}>
          {/* Mockup (left) */}
          <Box className="omni-feature-visual omni-reveal" sx={{ order: { xs: 2, md: 1 } }}>
            <Box className="omni-mockup-frame" sx={{ overflowX: { xs: 'auto', md: 'hidden' } }}>
              <Box className="omni-mockup-chrome">
                <Box className="omni-mockup-dots"><span /><span /><span /></Box>
                <Box className="omni-mockup-title">{t(`${m}.title`)}</Box>
              </Box>
              <Box sx={{ p: 2, minHeight: { xs: 'auto', md: 380 }, overflow: { xs: 'auto', md: 'hidden' }, minWidth: { xs: 480, md: 'auto' } }}>
                {/* Header + Tabs */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '12px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: 18 }}>✨</span>
                    <span style={{ fontWeight: 700, fontSize: 18, color: '#0f1729' }}>{t(`${m}.reportTitle`)}</span>
                  </Box>
                  <span style={{ fontSize: 15, color: '#6b7280', background: '#f3f4f6', padding: '4px 10px', borderRadius: 4 }}>{t(`${m}.date`)}</span>
                </Box>
                <Box sx={{ display: 'flex', gap: 0, mb: '14px', borderBottom: '2px solid #f0f0f0' }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#3664D1', padding: '6px 14px', borderBottom: '2px solid #3664D1', marginBottom: -2 }}>{t(`${m}.tabDaily`)}</span>
                  <span style={{ fontSize: 15, fontWeight: 500, color: '#6b7280', padding: '6px 14px' }}>{t(`${m}.tabWeekly`)}</span>
                  <span style={{ fontSize: 15, fontWeight: 500, color: '#6b7280', padding: '6px 14px' }}>{t(`${m}.tabMonthly`)}</span>
                </Box>

                {/* 4 Stat Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: '8px', mb: '14px' }}>
                  {[
                    { key: 'statConv', emoji: '💬', bg: '#e8f5e9' },
                    { key: 'statHuman', emoji: '🧑', bg: '#fff3e0' },
                    { key: 'statMsg', emoji: '📩', bg: '#EBF0FF' },
                    { key: 'statNew', emoji: '👤', bg: '#e8f5e9' },
                  ].map(({ key, emoji, bg }) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '10px', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{emoji}</Box>
                      <Box>
                        <Box sx={{ fontSize: 15, color: '#6b7280' }}>{t(`${m}.${key}.label`)}</Box>
                        <Box sx={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: '#0f1729' }}>{t(`${m}.${key}.value`)}</Box>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* AI Summary + Hot Topics */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: '10px', mb: '14px' }}>
                  <Box sx={{ p: '12px', borderRadius: '8px', background: '#f8faff', border: '1px solid #e8eeff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '6px' }}>
                      <span style={{ fontSize: 15 }}>✨</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#0f1729' }}>{t(`${m}.aiSummaryTitle`)}</span>
                    </Box>
                    <Box sx={{ fontSize: 15, color: '#374151', lineHeight: 1.7 }}>{t(`${m}.aiSummaryText`)}</Box>
                  </Box>
                  <Box sx={{ p: '12px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                    <Box sx={{ fontSize: 15, fontWeight: 700, color: '#0f1729', mb: '8px' }}>{t(`${m}.hotTopicsTitle`)}</Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {[
                        { key: 'hotTopic1', bg: '#e3f2fd', color: '#1565c0' },
                        { key: 'hotTopic2', bg: '#e8f5e9', color: '#2e7d32' },
                        { key: 'hotTopic3', bg: '#fff3e0', color: '#e65100' },
                        { key: 'hotTopic4', bg: '#fce4ec', color: '#c62828' },
                        { key: 'hotTopic5', bg: '#f3e5f5', color: '#6a1b9a' },
                      ].map(({ key, bg, color }) => (
                        <span key={key} style={{ fontSize: 14, padding: '4px 10px', borderRadius: 12, background: bg, color, fontWeight: 500 }}>{t(`${m}.${key}`)}</span>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Action Items + Suggestions */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '10px' }}>
                  <Box sx={{ p: '12px', borderRadius: '8px', background: '#fff8e1', border: '1px solid #ffe0b2' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '8px' }}>
                      <span style={{ fontSize: 15 }}>⚠️</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#0f1729' }}>{t(`${m}.attentionTitle`)}</span>
                    </Box>
                    {['1', '2'].map(n => (
                      <Box key={n} sx={{ display: 'flex', gap: '6px', alignItems: 'flex-start', mb: n === '1' ? '8px' : 0 }}>
                        <span style={{ fontSize: 14, color: '#f57c00', marginTop: 2 }}>●</span>
                        <Box sx={{ fontSize: 15, color: '#374151', lineHeight: 1.5 }}>
                          <strong>{t(`${m}.attention${n}Title`)}</strong><br />
                          {t(`${m}.attention${n}Text`)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ p: '12px', borderRadius: '8px', background: '#e3f2fd', border: '1px solid #bbdefb' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '8px' }}>
                      <span style={{ fontSize: 15 }}>💡</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#0f1729' }}>{t(`${m}.suggestionsTitle`)}</span>
                    </Box>
                    {['1', '2'].map(n => (
                      <Box key={n} sx={{ display: 'flex', gap: '6px', alignItems: 'flex-start', mb: n === '1' ? '8px' : 0 }}>
                        <span style={{ fontSize: 14, color: '#1976d2', marginTop: 2 }}>●</span>
                        <Box sx={{ fontSize: 15, color: '#374151', lineHeight: 1.5 }}>
                          <strong>{t(`${m}.suggestion${n}Title`)}</strong><br />
                          {t(`${m}.suggestion${n}Text`)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Text (right) */}
          <Box className="omni-reveal omni-reveal-delay-1" sx={{ order: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#10b981', mb: 2 }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              {t('featureReport.label')}
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: 'clamp(1.6rem, 3vw, 2.2rem)' }, fontWeight: 800, lineHeight: 1.3, mb: 2, letterSpacing: '-0.3px', color: '#0f1729' }}>
              {t('featureReport.title')}
            </Typography>
            <Typography sx={{ fontSize: 19, color: '#374151', lineHeight: 1.8, mb: '28px' }}>
              {t('featureReport.desc')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {['bullet1', 'bullet2', 'bullet3'].map((key) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '2px', fontSize: 15, fontWeight: 700, color: '#fff' }}>&#10003;</Box>
                  <Typography sx={{ fontSize: 18, color: '#374151', lineHeight: 1.5 }}>{t(`featureReport.${key}`)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
